"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Banner 1025 — BDU Hero.
 *
 * Port of the BDU v1 `components/Home/Hero.tsx` + `components/organisms/Banner.tsx`
 * pair: a full-bleed slide carousel with a left overlay card (gradient panel on the
 * first slide only), title / subtitle / single CTA, and an accreditation badge bar
 * pinned to the bottom of the card.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:` is
 * 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`). Every `md:` in the reference is therefore ported as
 * `tab:` here; using `md:` would delay the desktop layout to 1230px and break the
 * 768–1229px range.
 *
 * The reference's `md:ml-22` is NOT a real Tailwind class (there is no `22` in the
 * spacing scale) and emits no rule in the live production CSS, so the card's left
 * offset is `mx-5` (20px) at every width. That rendered behaviour is reproduced here
 * rather than the apparent intent.
 */

interface HeroSlide {
  id?: string | number;
  image?: string;
  alt_image?: string;
  /** Optional; falls back to `image` when empty */
  mobile_image?: string;
  mobile_alt_image?: string;
  title?: string;
  subtitle?: string;
  title_color?: string;
  subtitle_color?: string;
  priority?: number;
  status?: boolean;
}

interface AccLogo {
  id?: string | number;
  image?: string;
  alt_image?: string;
  priority?: number;
  status?: boolean;
}

interface Banner1025Data {
  /** Carousel slides */
  metadata?: HeroSlide[];
  /** Accreditation badges — section level, rendered on every slide that has a title */
  metadata_1?: AccLogo[];
  slide_frequency?: number;
  hero_section_height?: number;
  /** Overlay panel gradient (first slide, tab and up) */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Overlay panel fill below `tab` */
  background_color?: string;
  /** CTA gradient */
  gradient_color_3?: string;
  gradient_color_4?: string;
  apply_now_show?: boolean;
  apply_now_text?: string;
  apply_now_link?: string;
  apply_now_is_cta?: boolean;
  apply_now_color?: string;
  /** Accreditation bar background */
  horizontal_stripe_color?: string;
  status?: boolean;
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

function sortActive<T extends { priority?: number; status?: boolean }>(
  rows: T[] | undefined,
): T[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r) => r?.status !== false)
    .sort((a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0));
}

const Banner1025 = (props: { data?: Banner1025Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Banner1025Data | undefined;
  const dispatch = useDispatch();
  const [index, setIndex] = useState(0);

  const {
    slide_frequency = 5000,
    hero_section_height = 540,
    gradient_color_1 = "#13344F",
    gradient_color_2 = "#77B6EB",
    background_color = "#22405ADB",
    gradient_color_3 = "#FF0013",
    gradient_color_4 = "#990013",
    apply_now_text = "Enroll Now",
    apply_now_link = "",
    apply_now_color = "#FFFFFF",
    horizontal_stripe_color = "#FFFFFF",
  } = sectionData || {};

  const applyNowShow = isCmsShowFlagOn(sectionData?.apply_now_show, true);
  const applyNowIsCta = isCmsShowFlagOn(sectionData?.apply_now_is_cta, true);
  const status = isCmsShowFlagOn(sectionData?.status, true);

  const slides = useMemo(
    () => sortActive(sectionData?.metadata),
    [sectionData?.metadata],
  );
  const accLogos = useMemo(
    () => sortActive(sectionData?.metadata_1).filter((a) => !!a?.image),
    [sectionData?.metadata_1],
  );

  const n = slides.length;

  // Autoplay — v1 advances on a fixed interval with no pause-on-hover.
  useEffect(() => {
    if (!status || n <= 1 || !slide_frequency) return undefined;
    const id = window.setInterval(
      () => setIndex((prev) => (prev === n - 1 ? 0 : prev + 1)),
      Number(slide_frequency),
    );
    return () => window.clearInterval(id);
  }, [status, n, slide_frequency]);

  // A slide count change (CMS edit / preview) can leave `index` past the end.
  useEffect(() => {
    if (n > 0 && index > n - 1) setIndex(0);
  }, [n, index]);

  const handleApplyNowClick = useCallback(() => {
    ctaClick({
      section: "banner_1025",
      component: "hero_apply_now",
      label: apply_now_text?.trim() || "apply_now",
      step: "1",
      entity: "banner_1025",
      parentEntity: "banner_1025",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [apply_now_text, dispatch]);

  if (!status || n === 0) return null;

  const ctaClassName = cn(
    "inline-flex items-center justify-center whitespace-nowrap rounded-md",
    "shadow transition-colors",
    "h-auto w-[170px] px-5 py-2",
    "text-base font-medium tab:text-lg",
  );
  const ctaStyle = {
    backgroundImage: `linear-gradient(to right, ${gradient_color_3}, ${gradient_color_4})`,
    color: apply_now_color,
  };

  return (
    <section
      className="relative h-[402px] w-full tab:h-[var(--bdu-hero-h)]"
      style={{ ["--bdu-hero-h" as string]: `${hero_section_height}px` }}
    >
      <div className="relative size-full overflow-hidden border-none">
        <div className="relative size-full">
          {slides.map((slide, i) => {
            const desktopSrc = slide.image || slide.mobile_image || "";
            const mobileSrc = slide.mobile_image || slide.image || "";
            const isFirst = i === 0;

            return (
              <div
                key={slide.id ?? i}
                className={cn(
                  "absolute inset-0 h-full w-full transition-transform duration-700 ease-in-out",
                  index === i ? "z-10 translate-x-0" : "z-0",
                  index < i && "translate-x-full",
                  index > i && "-translate-x-full",
                )}
              >
                {/* Desktop image */}
                <Image
                  src={desktopSrc}
                  alt={slide.alt_image || `Slide ${i + 1}`}
                  width={800}
                  height={540}
                  priority={isFirst}
                  loading={isFirst ? "eager" : "lazy"}
                  quality={85}
                  fetchPriority={isFirst ? "high" : "auto"}
                  sizes="100vw"
                  className="hidden size-full object-cover object-top tab:block"
                />
                {/* Mobile image */}
                <Image
                  src={mobileSrc}
                  alt={slide.mobile_alt_image || slide.alt_image || `Slide ${i + 1}`}
                  width={800}
                  height={402}
                  priority={isFirst}
                  loading={isFirst ? "eager" : "lazy"}
                  quality={85}
                  fetchPriority={isFirst ? "high" : "auto"}
                  sizes="100vw"
                  className="size-full object-cover object-top tab:hidden"
                />

                <div
                  className={cn(
                    "absolute inset-0 transition-opacity duration-700",
                    index === i ? "opacity-100" : "opacity-0",
                  )}
                >
                  {slide.title ? (
                    <div
                      className={cn(
                        "absolute inset-0 flex size-full w-full rounded-none tab:w-[560px]",
                      )}
                      // Only the first slide carries the panel fill in the reference.
                      style={
                        isFirst
                          ? {
                              backgroundColor: background_color,
                              backgroundImage: undefined,
                            }
                          : undefined
                      }
                    >
                      {isFirst && (
                        <div
                          aria-hidden
                          className="absolute inset-0 hidden size-full tab:block"
                          style={{
                            backgroundImage: `linear-gradient(90deg, ${gradient_color_1}, ${gradient_color_2})`,
                          }}
                        />
                      )}

                      <div
                        className={cn(
                          "relative flex w-full items-center p-0",
                          "mx-5 mb-8 mt-[72px] h-[298px]",
                          "tab:mb-14 tab:mt-[88px] tab:h-[398px] tab:w-[500px]",
                        )}
                      >
                        <div className="flex h-full flex-col items-start justify-between text-left text-white">
                          {isFirst ? (
                            <h1
                              className="w-[218px] text-2xl font-bold tab:w-[454px] tab:text-[42px]/[50px]"
                              style={{ color: slide.title_color || "#FFFFFF" }}
                            >
                              {slide.title}
                            </h1>
                          ) : (
                            <p
                              className="w-[218px] text-2xl font-bold tab:w-[454px] tab:text-[42px]/[50px]"
                              style={{ color: slide.title_color || "#FFFFFF" }}
                            >
                              {slide.title}
                            </p>
                          )}

                          {slide.subtitle && (
                            <p
                              className={cn(
                                "mt-1.5 w-[201px] flex-wrap text-sm/[21px] font-normal",
                                "tab:w-[468px] tab:text-lg/[27px] tab:leading-7",
                              )}
                              style={{
                                color: slide.subtitle_color || "#FFFFFF",
                              }}
                            >
                              {slide.subtitle}
                            </p>
                          )}

                          {applyNowShow && apply_now_text && (
                            <div className="mt-[18px] flex flex-col gap-2 tab:mt-8 tab:flex-row">
                              {applyNowIsCta || !apply_now_link ? (
                                <button
                                  type="button"
                                  onClick={handleApplyNowClick}
                                  className={ctaClassName}
                                  style={ctaStyle}
                                >
                                  {apply_now_text}
                                </button>
                              ) : (
                                <Link
                                  href={normalizeHref(apply_now_link)}
                                  className={ctaClassName}
                                  style={ctaStyle}
                                >
                                  {apply_now_text}
                                </Link>
                              )}
                            </div>
                          )}

                          {accLogos.length > 0 && (
                            <div
                              className={cn(
                                "mt-auto flex h-10 w-auto flex-row items-center justify-center rounded",
                                "tab:h-[62px] tab:justify-start tab:rounded-md",
                              )}
                              style={{
                                backgroundColor: horizontal_stripe_color,
                              }}
                            >
                              {accLogos.map((logo, li) => (
                                <div
                                  key={logo.id ?? li}
                                  className="flex h-full items-center justify-center p-1.5 tab:max-w-[125px] tab:p-2.5"
                                >
                                  <Image
                                    src={logo.image || ""}
                                    alt={logo.alt_image || ""}
                                    width={60}
                                    height={24}
                                    className="h-7 w-full tab:h-[50px]"
                                    priority
                                    loading="eager"
                                    quality={85}
                                    fetchPriority="high"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Banner1025;
